(function () {
  'use strict';

  var contentBoundsCache = {};
  var EXTRA_INTERLOCK_RATIO = 0.08;
  var HANDOFF_KEY = 'candlStackHandoff';
  var HANDOFF_TTL_MS = 10 * 60 * 1000;
  var handoffConsumed = false;

  function getContentBounds(src) {
    if (contentBoundsCache[src]) return contentBoundsCache[src];
    var promise = new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var sampleSize = 80;
          var ratio = img.naturalHeight / img.naturalWidth;
          var w = sampleSize;
          var h = Math.max(1, Math.round(sampleSize * ratio));
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          var data = ctx.getImageData(0, 0, w, h).data;
          var top = null;
          var bottom = null;
          for (var y = 0; y < h; y++) {
            var rowHasContent = false;
            for (var x = 0; x < w; x++) {
              if (data[(y * w + x) * 4 + 3] > 10) { rowHasContent = true; break; }
            }
            if (rowHasContent) {
              if (top === null) top = y;
              bottom = y;
            }
          }
          if (top === null) {
            resolve({ topPct: 0, bottomPct: 100 });
          } else {
            resolve({ topPct: (top / h) * 100, bottomPct: (bottom / h) * 100 });
          }
        } catch (e) {
          resolve({ topPct: 0, bottomPct: 100 });
        }
      };
      img.onerror = function () {
        resolve({ topPct: 0, bottomPct: 100 });
      };
      img.src = src;
    });
    contentBoundsCache[src] = promise;
    return promise;
  }

  function loadImageEl(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image failed to load')); };
      img.src = src;
    });
  }

  function stackLayers(preview) {
    var layers = Array.prototype.slice.call(preview.querySelectorAll('.stack-builder__preview-layer'));
    if (layers.length === 0) return Promise.resolve();
    var imgs = layers.map(function (layer) { return layer.querySelector('img'); });

    function apply() {
      return Promise.all(imgs.map(function (img) { return getContentBounds(img.src); })).then(function (boundsList) {
        layers.forEach(function (layer, idx) {
          if (idx === 0) {
            layer.style.marginTop = '0';
            return;
          }
          var prevLayer = layers[idx - 1];
          var prevBounds = boundsList[idx - 1];
          var bounds = boundsList[idx];
          var prevHeight = prevLayer.getBoundingClientRect().height || prevLayer.offsetHeight;
          var height = layer.getBoundingClientRect().height || layer.offsetHeight;
          var prevBottomPad = ((100 - prevBounds.bottomPct) / 100) * prevHeight;
          var topPad = (bounds.topPct / 100) * height;
          var overlap = prevBottomPad + topPad + (height * EXTRA_INTERLOCK_RATIO);
          layer.style.marginTop = '-' + Math.max(0, overlap) + 'px';
        });
      });
    }

    var pending = imgs.filter(function (img) { return !img.complete; });
    if (pending.length === 0) {
      return apply();
    }
    return new Promise(function (resolveOuter) {
      var remaining = pending.length;
      pending.forEach(function (img) {
        img.addEventListener('load', function () {
          remaining -= 1;
          if (remaining === 0) apply().then(resolveOuter);
        });
        img.addEventListener('error', function () {
          remaining -= 1;
          if (remaining === 0) apply().then(resolveOuter);
        });
      });
    });
  }

  function initStackBuilder(root) {
    if (root.__stackBuilderInit) return;
    root.__stackBuilderInit = true;

    var config = {};
    try {
      config = JSON.parse(root.getAttribute('data-stack-config') || '{}');
    } catch (e) {
      config = {};
    }
    var labels = config.labels || {};
    var palettes = config.palettes || [];

    var grid = root.querySelector('[data-stack-grid]');
    var filters = root.querySelector('[data-stack-filters]');
    var tabs = root.querySelectorAll('[data-stack-tab]');
    var preview = root.querySelector('[data-stack-preview]');
    var emptyState = root.querySelector('[data-stack-empty]');
    var list = root.querySelector('[data-stack-list]');
    var countEl = root.querySelector('[data-stack-count]');
    var priceEl = root.querySelector('[data-stack-price]');
    var addToCartBtn = root.querySelector('[data-stack-add-to-cart]');
    var addToCartLabel = root.querySelector('[data-stack-add-to-cart-label]');
    var errorEl = root.querySelector('[data-stack-error]');
    var isCompact = root.classList.contains('stack-builder--compact');
    var compactCta = root.querySelector('.stack-builder__compact-cta');
    var toastEl = ensureToast();
    var resetBtn = ensureResetButton();
    var downloadBtn = ensureDownloadButton();
    var mobileBar = ensureMobileBar();
    var mobileThumb = ensureMobileThumb();
    var mobileThumbInner = mobileThumb.querySelector('[data-mobile-thumb-inner]');
    var mobileCountEl = mobileBar.querySelector('[data-mobile-count]');
    var mobilePriceEl = mobileBar.querySelector('[data-mobile-price]');
    var mobileAddBtn = mobileBar.querySelector('[data-mobile-add-btn]');
    var mobileAddLabel = mobileBar.querySelector('[data-mobile-add-label]');
    mobileAddLabel.textContent = addToCartLabel ? addToCartLabel.textContent : (labels.addToCart || '');
    var inlineCta = ensureInlineAddButton();
    var inlineAddBtn = inlineCta.querySelector('[data-stack-add-inline]');
    var inlineAddLabel = inlineCta.querySelector('[data-stack-add-inline-label]');
    inlineAddLabel.textContent = addToCartLabel ? addToCartLabel.textContent : (labels.addToCart || '');
    var giftWrapCheckbox = root.querySelector('[data-stack-gift-wrap]');
    var giftMessageToggle = root.querySelector('[data-stack-gift-message-toggle]');
    var giftMessageInput = root.querySelector('[data-stack-gift-message-input]');
    var paletteInspiration = root.querySelector('[data-stack-palette-inspiration]');
    var paletteInspirationImg = paletteInspiration ? paletteInspiration.querySelector('[data-stack-palette-inspiration-img]') : null;
    if (giftMessageToggle && giftMessageInput) {
      giftMessageToggle.addEventListener('change', function () {
        giftMessageInput.hidden = !giftMessageToggle.checked;
        if (giftMessageToggle.checked) giftMessageInput.focus();
      });
    }

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-stack-item]')).map(function (el) {
      return {
        el: el,
        moduleId: el.getAttribute('data-module-id'),
        moduleLabel: el.getAttribute('data-module-label'),
        productHandle: el.getAttribute('data-product-handle'),
        role: el.getAttribute('data-role'),
        variantId: el.getAttribute('data-variant-id'),
        color: el.getAttribute('data-color'),
        price: parseFloat(el.getAttribute('data-price')) || 0,
        priceFormatted: el.getAttribute('data-price-formatted'),
        image: el.getAttribute('data-image'),
        available: el.getAttribute('data-available') === 'true'
      };
    });

    var stack = [];
    var currentTab = (isCompact && palettes.length > 0) ? 'palette' : 'shape';
    var activeFilter = 'all';
    var thumbUpdateToken = 0;

    // Drag-to-reposition for the floating tower preview, now free in both
    // directions (not just left/right) since .thumb is position: fixed to
    // the viewport (see the CSS comment on .stack-builder__mobile-bar-thumb).
    // The drag target is .thumb-inner itself — the one element with
    // pointer-events: auto — and dragging is the explicit, reliable way to
    // clear a tile rather than hoping click-through works on every
    // browser/device. Offsets are written as --drag-x/--drag-y custom
    // properties on .thumb, which the CSS folds into its base centering
    // transform via calc(), so centering and dragging share one transform
    // instead of fighting over it.
    var thumbDragX = 0;
    var thumbDragY = 0;

    function initThumbDrag() {
      if (!mobileThumbInner || !mobileThumb) return;
      var pointerId = null;
      var startClientX = 0;
      var startClientY = 0;
      var startDragX = 0;
      var startDragY = 0;
      var minX = 0;
      var maxX = 0;
      var minY = 0;
      var maxY = 0;
      var dragMoved = false;

      mobileThumbInner.addEventListener('pointerdown', function (e) {
        if (typeof e.button === 'number' && e.button !== 0) return;
        pointerId = e.pointerId;
        startClientX = e.clientX;
        startClientY = e.clientY;
        startDragX = thumbDragX;
        startDragY = thumbDragY;
        dragMoved = false;
        var rect = mobileThumbInner.getBoundingClientRect();
        var margin = 8;
        minX = thumbDragX - (rect.left - margin);
        maxX = thumbDragX + (window.innerWidth - rect.right - margin);
        minY = thumbDragY - (rect.top - margin);
        maxY = thumbDragY + (window.innerHeight - rect.bottom - margin);
        if (maxX < minX) maxX = minX;
        if (maxY < minY) maxY = minY;
        document.addEventListener('pointermove', onThumbDragMove);
        document.addEventListener('pointerup', onThumbDragEnd);
        document.addEventListener('pointercancel', onThumbDragEnd);
      });

      function onThumbDragMove(e) {
        if (e.pointerId !== pointerId) return;
        var dx = e.clientX - startClientX;
        var dy = e.clientY - startClientY;
        if (!dragMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) dragMoved = true;
        if (!dragMoved) return;
        e.preventDefault();
        var nextX = startDragX + dx;
        var nextY = startDragY + dy;
        if (nextX < minX) nextX = minX;
        if (nextX > maxX) nextX = maxX;
        if (nextY < minY) nextY = minY;
        if (nextY > maxY) nextY = maxY;
        thumbDragX = nextX;
        thumbDragY = nextY;
        mobileThumb.style.setProperty('--drag-x', thumbDragX + 'px');
        mobileThumb.style.setProperty('--drag-y', thumbDragY + 'px');
      }

      function onThumbDragEnd(e) {
        if (e.pointerId !== pointerId) return;
        document.removeEventListener('pointermove', onThumbDragMove);
        document.removeEventListener('pointerup', onThumbDragEnd);
        document.removeEventListener('pointercancel', onThumbDragEnd);
        pointerId = null;
      }
    }

    function resetThumbDrag() {
      thumbDragX = 0;
      thumbDragY = 0;
      if (mobileThumb) {
        mobileThumb.style.removeProperty('--drag-x');
        mobileThumb.style.removeProperty('--drag-y');
      }
    }

    function moduleLabels() {
      var seen = {};
      var out = [];
      items.forEach(function (i) {
        if (!seen[i.moduleLabel]) {
          seen[i.moduleLabel] = true;
          out.push(i.moduleLabel);
        }
      });
      return out;
    }

    function colorLabels() {
      var seen = {};
      var out = [];
      items.forEach(function (i) {
        if (i.color && !seen[i.color]) {
          seen[i.color] = true;
          out.push(i.color);
        }
      });
      return out;
    }

    function renderFilters() {
      var options = [];
      if (currentTab === 'shape') {
        options = moduleLabels();
      } else if (currentTab === 'color') {
        options = colorLabels();
      } else if (currentTab === 'palette') {
        options = palettes.map(function (p) { return p.title; });
      }

      activeFilter = 'all';
      var html = '<button type="button" class="stack-builder__filter is-active" data-filter-value="all">' +
        (labels.allModules || 'All modules') + '</button>';
      options.forEach(function (opt) {
        html += '<button type="button" class="stack-builder__filter" data-filter-value="' +
          escapeAttr(opt) + '">' + escapeHtml(opt) + '</button>';
      });
      filters.innerHTML = html;

      filters.querySelectorAll('[data-filter-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          filters.querySelectorAll('.stack-builder__filter').forEach(function (b) {
            b.classList.remove('is-active');
          });
          btn.classList.add('is-active');
          activeFilter = btn.getAttribute('data-filter-value');
          applyFilter();
          updatePaletteInspiration();
        });
      });

      applyFilter();
      updatePaletteInspiration();
    }

    function updatePaletteInspiration() {
      if (!paletteInspiration || !paletteInspirationImg) return;
      if (currentTab !== 'palette' || activeFilter === 'all') {
        paletteInspiration.hidden = true;
        return;
      }
      var match = palettes.filter(function (p) { return p.title === activeFilter; })[0];
      if (match && match.image) {
        paletteInspirationImg.src = match.image;
        paletteInspirationImg.alt = match.title || '';
        paletteInspiration.hidden = false;
      } else {
        paletteInspiration.hidden = true;
      }
    }

    function applyFilter() {
      var paletteColors = null;
      if (currentTab === 'palette' && activeFilter !== 'all') {
        var match = palettes.filter(function (p) { return p.title === activeFilter; })[0];
        paletteColors = match ? match.colors.map(function (c) { return String(c).toLowerCase(); }) : [];
      }

      items.forEach(function (i) {
        var visible = true;
        if (activeFilter !== 'all') {
          if (currentTab === 'shape') visible = i.moduleLabel === activeFilter;
          else if (currentTab === 'color') visible = i.color === activeFilter;
          else if (currentTab === 'palette') visible = paletteColors && paletteColors.indexOf(String(i.color || '').toLowerCase()) !== -1;
        }
        i.el.hidden = !visible;
      });
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : str;
      return div.innerHTML;
    }

    function escapeAttr(str) {
      return String(str == null ? '' : str).replace(/"/g, '&quot;');
    }

    function groupThousands(digits) {
      return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    function formatMoney(cents) {
      var moneyFormat = config.moneyFormat || '{{amount}} EUR';
      var major = cents / 100;
      var whole = Math.round(major).toString();
      var fixed = major.toFixed(2).split('.');

      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
        var useComma = token.indexOf('comma_separator') !== -1;
        var noDecimals = token.indexOf('no_decimals') !== -1;
        if (noDecimals) {
          return useComma ? groupThousands(whole) : whole;
        }
        return useComma ? groupThousands(fixed[0]) + ',' + fixed[1] : fixed[0] + '.' + fixed[1];
      });
    }

    function addItem(item) {
      if (!item.available) return;
      if (item.role === 'top') {
        stack = stack.filter(function (s) { return s.role !== 'top'; });
        stack.unshift(Object.assign({}, item, { key: item.variantId + '-' + Date.now() }));
      } else {
        stack.push(Object.assign({}, item, { key: item.variantId + '-' + Date.now() + '-' + Math.random() }));
      }
      render();
    }

    function removeStackKey(key) {
      stack = stack.filter(function (s) { return s.key !== key; });
      render();
    }

    function saveHandoff() {
      try {
        if (stack.length === 0) {
          localStorage.removeItem(HANDOFF_KEY);
          return;
        }
        var payload = {
          ts: Date.now(),
          items: stack.map(function (s) { return { variantId: s.variantId, role: s.role }; })
        };
        localStorage.setItem(HANDOFF_KEY, JSON.stringify(payload));
      } catch (e) {
      }
    }

    function applyHandoffIfAny() {
      if (handoffConsumed) return;
      handoffConsumed = true;
      var raw;
      try {
        raw = localStorage.getItem(HANDOFF_KEY);
      } catch (e) {
        return;
      }
      if (!raw) return;
      var payload = null;
      try {
        payload = JSON.parse(raw);
      } catch (e) {
        payload = null;
      }
      try {
        localStorage.removeItem(HANDOFF_KEY);
      } catch (e) {
      }
      if (!payload || !payload.items || !payload.items.length) return;
      if (!payload.ts || (Date.now() - payload.ts) > HANDOFF_TTL_MS) return;

      var byVariant = {};
      items.forEach(function (i) { byVariant[i.variantId] = i; });

      var applied = false;
      payload.items.forEach(function (saved) {
        var match = byVariant[saved.variantId];
        if (match) {
          addItem(match);
          applied = true;
        }
      });
      if (applied) {
        showToast(labels.handoffRestored || 'Your build from the homepage was carried over');
      }
    }

    function clearDropIndicators() {
      root.querySelectorAll('.drag-over-before, .drag-over-after').forEach(function (el) {
        el.classList.remove('drag-over-before', 'drag-over-after');
      });
    }

    function moveStackItem(sourceKey, targetKey, placeAfter) {
      var top = stack.filter(function (s) { return s.role === 'top'; });
      var rest = stack.filter(function (s) { return s.role !== 'top'; });
      var fromIndex = -1;
      rest.forEach(function (s, idx) { if (s.key === sourceKey) fromIndex = idx; });
      if (fromIndex === -1) return;
      var moved = rest.splice(fromIndex, 1)[0];
      var toIndex = -1;
      rest.forEach(function (s, idx) { if (s.key === targetKey) toIndex = idx; });
      if (toIndex === -1) {
        rest.push(moved);
      } else {
        rest.splice(placeAfter ? toIndex + 1 : toIndex, 0, moved);
      }
      stack = top.concat(rest);
      render();
    }

    function findDropTarget(container, itemEl, clientY) {
      var siblings = Array.prototype.filter.call(container.children, function (el) {
        return el !== itemEl && el.hasAttribute('data-drag-key');
      });
      for (var i = 0; i < siblings.length; i++) {
        var rect = siblings[i].getBoundingClientRect();
        if (clientY >= rect.top && clientY <= rect.bottom) {
          return {
            key: siblings[i].getAttribute('data-drag-key'),
            before: (clientY - rect.top) < rect.height / 2
          };
        }
      }
      return null;
    }

    function makeDraggable(handleEl, itemEl, key, container) {
      itemEl.setAttribute('data-drag-key', key);
      itemEl.classList.add('is-draggable');
      handleEl.style.touchAction = 'none';

      var pointerId = null;
      var startX = 0;
      var startY = 0;
      var dragging = false;
      var dropTarget = null;

      function onMove(e) {
        if (e.pointerId !== pointerId) return;
        if (!dragging) {
          if (Math.abs(e.clientY - startY) < 6 && Math.abs(e.clientX - startX) < 6) return;
          dragging = true;
          itemEl.classList.add('is-dragging');
        }
        e.preventDefault();
        clearDropIndicators();
        dropTarget = findDropTarget(container, itemEl, e.clientY);
        if (dropTarget) {
          var targetEl = container.querySelector('[data-drag-key="' + dropTarget.key + '"]');
          if (targetEl) targetEl.classList.add(dropTarget.before ? 'drag-over-before' : 'drag-over-after');
        }
      }

      function onUp(e) {
        if (e.pointerId !== pointerId) return;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        itemEl.classList.remove('is-dragging');
        clearDropIndicators();
        if (dragging && dropTarget) {
          moveStackItem(key, dropTarget.key, !dropTarget.before);
        }
        dragging = false;
        dropTarget = null;
        pointerId = null;
      }

      handleEl.addEventListener('pointerdown', function (e) {
        if (typeof e.button === 'number' && e.button !== 0) return;
        pointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        dragging = false;
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
      });
    }

    function ensureToast() {
      var existing = root.querySelector('[data-stack-toast]');
      if (existing) return existing;
      var previewWrap = root.querySelector('.stack-builder__preview');
      var toast = document.createElement('div');
      toast.className = 'stack-builder__toast';
      toast.setAttribute('data-stack-toast', '');
      previewWrap.insertBefore(toast, previewWrap.firstChild);
      return toast;
    }

    var toastTimer = null;
    function showToast(message) {
      toastEl.textContent = message;
      toastEl.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toastEl.classList.remove('is-visible');
      }, 3200);
    }

    function ensureResetButton() {
      var existing = root.querySelector('[data-stack-reset]');
      if (existing) return existing;
      var totals = root.querySelector('.stack-builder__totals');
      var countHolder = root.querySelector('[data-stack-count]');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'stack-builder__reset';
      btn.setAttribute('data-stack-reset', '');
      btn.hidden = true;
      btn.textContent = labels.startOver || 'Start over';
      if (countHolder && countHolder.parentNode === totals) {
        totals.insertBefore(btn, countHolder.nextSibling);
      } else {
        totals.appendChild(btn);
      }
      return btn;
    }

    function resetStack() {
      if (stack.length === 0) return;
      stack = [];
      render();
    }

    function ensureDownloadButton() {
      var existing = root.querySelector('[data-stack-download]');
      if (existing) return existing;
      var previewInner = root.querySelector('.stack-builder__preview-inner');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'stack-builder__download';
      btn.setAttribute('data-stack-download', '');
      btn.setAttribute('aria-label', labels.downloadImage || 'Download image');
      btn.hidden = true;
      btn.innerHTML =
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M4.5 16.5V18a2.5 2.5 0 0 0 2.5 2.5h10a2.5 2.5 0 0 0 2.5-2.5v-1.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      previewInner.appendChild(btn);
      return btn;
    }

    function higherResSrc(src, targetWidth) {
      if (!src) return src;
      if (/[?&]width=\d+/.test(src)) {
        return src.replace(/([?&]width=)\d+/, '$1' + targetWidth);
      }
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + targetWidth;
    }

    function buildDownloadCanvas() {
      var top = stack.filter(function (s) { return s.role === 'top'; });
      var rest = stack.filter(function (s) { return s.role !== 'top'; });
      var ordered = top.concat(rest);
      if (ordered.length === 0) return Promise.reject(new Error('empty stack'));

      var HQ_WIDTH = 1200;
      var TARGET_W = 900;
      var PADDING_X = 140;
      var PADDING_TOP = 90;
      var PADDING_BOTTOM = 130;

      return Promise.all(ordered.map(function (s) {
        var src = higherResSrc(s.image, HQ_WIDTH);
        return Promise.all([loadImageEl(src), getContentBounds(src)]).then(function (res) {
          return { item: s, img: res[0], bounds: res[1] };
        });
      })).then(function (layers) {
        layers.forEach(function (l) {
          l.dw = TARGET_W;
          l.dh = TARGET_W * (l.img.naturalHeight / l.img.naturalWidth);
        });

        var positions = [];
        var cursorY = 0;
        layers.forEach(function (l, idx) {
          if (idx === 0) {
            l.y = 0;
          } else {
            var prev = layers[idx - 1];
            var prevBottomPad = ((100 - prev.bounds.bottomPct) / 100) * prev.dh;
            var topPad = (l.bounds.topPct / 100) * l.dh;
            var overlap = prevBottomPad + topPad + (l.dh * EXTRA_INTERLOCK_RATIO);
            l.y = prev.y + prev.dh - Math.max(0, overlap);
          }
          positions.push(l.y);
          positions.push(l.y + l.dh);
        });

        var stackTop = Math.min.apply(null, positions);
        var stackBottom = Math.max.apply(null, positions);
        var stackHeight = stackBottom - stackTop;

        var canvasW = TARGET_W + PADDING_X * 2;
        var canvasH = stackHeight + PADDING_TOP + PADDING_BOTTOM;
        var canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        var ctx = canvas.getContext('2d');

        var bgColor = (getComputedStyle(preview).getPropertyValue('--sb-bg') || '#f7f5f1').trim() || '#f7f5f1';
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvasW, canvasH);

        var centerX = canvasW / 2;
        var baseY = PADDING_TOP + stackHeight;

        var shadowW = TARGET_W * 0.85;
        var grad = ctx.createRadialGradient(centerX, baseY, 4, centerX, baseY, shadowW / 2);
        grad.addColorStop(0, 'rgba(20,16,12,0.22)');
        grad.addColorStop(1, 'rgba(20,16,12,0)');
        ctx.save();
        ctx.translate(0, baseY);
        ctx.scale(1, 0.22);
        ctx.translate(0, -baseY);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(centerX, baseY, shadowW / 2, shadowW / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        layers.forEach(function (l) {
          var dx = centerX - l.dw / 2;
          var dy = PADDING_TOP + (l.y - stackTop);
          ctx.drawImage(l.img, dx, dy, l.dw, l.dh);
        });

        ctx.fillStyle = 'rgba(26,26,26,0.55)';
        ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(labels.downloadWatermark || 'Built at staneditions.be', centerX, canvasH - 32);

        return canvas;
      });
    }

    function downloadStackImage() {
      if (stack.length === 0) return;
      downloadBtn.disabled = true;
      buildDownloadCanvas().then(function (canvas) {
        var link = document.createElement('a');
        link.download = 'candl-stack-' + Date.now() + '.png';
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }).catch(function () {
        showToast(labels.downloadError || 'Could not create the image, please try again.');
      }).finally(function () {
        downloadBtn.disabled = false;
      });
    }

    function updateMobileThumb() {
      thumbUpdateToken += 1;
      var token = thumbUpdateToken;
      if (!preview.classList.contains('has-stack')) {
        mobileThumb.hidden = true;
        mobileThumbInner.innerHTML = '';
        resetThumbDrag();
        return;
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (token !== thumbUpdateToken) return;
          if (!preview.classList.contains('has-stack')) return;
          var clone = preview.cloneNode(true);
          clone.querySelectorAll('[data-stack-download], .stack-builder__preview-layer-handle').forEach(function (el) {
            el.remove();
          });
          clone.removeAttribute('data-stack-preview');
          clone.removeAttribute('id');
          clone.className = 'stack-builder__mobile-bar-thumb-stack';
          mobileThumbInner.innerHTML = '';
          mobileThumbInner.appendChild(clone);
          mobileThumb.hidden = false;
        });
      });
    }

    // A fixed bottom bar on mobile mirroring count/price/add-to-cart, so the
    // shopper never has to scroll back up through the grid to check out.
    // Appended to <body> (not the section) so `position: fixed` isn't at the
    // mercy of any transformed ancestor in the theme.
    function ensureMobileBar() {
      var existing = document.querySelector('[data-stack-mobile-bar]');
      if (existing) return existing;
      var bar = document.createElement('div');
      bar.className = 'stack-builder__mobile-bar';
      bar.setAttribute('data-stack-mobile-bar', '');
      bar.innerHTML =
        '<div class="stack-builder__mobile-bar-info">' +
          '<div class="stack-builder__mobile-bar-count" data-mobile-count></div>' +
          '<div class="stack-builder__mobile-bar-price" data-mobile-price></div>' +
        '</div>' +
        '<button type="button" class="stack-builder__mobile-bar-btn" data-mobile-add-btn disabled>' +
          '<span data-mobile-add-label></span>' +
        '</button>';
      document.body.appendChild(bar);
      return bar;
    }

    // The floating tower thumb is a SIBLING of the bar, not a child of it,
    // and that's deliberate: .mobile-bar has its own `transform:
    // translateY(...)` for its slide-up/down show/hide animation, and per
    // the CSS spec, any element with a transform other than `none` becomes
    // the containing block for its position: fixed descendants. Nesting the
    // thumb inside the bar would silently make its "centered on screen"
    // left/top percentages resolve against the BAR's box instead of the
    // real viewport (e.g. "vertically centered" would end up glued near the
    // bottom, where the bar sits) -- exactly the bug this separate element
    // avoids. Appended straight to <body> for the same reason as the bar.
    function ensureMobileThumb() {
      var existing = document.querySelector('[data-mobile-thumb]');
      if (existing) return existing;
      var thumb = document.createElement('div');
      thumb.className = 'stack-builder__mobile-bar-thumb';
      thumb.setAttribute('data-mobile-thumb', '');
      thumb.hidden = true;
      thumb.innerHTML =
        '<div class="stack-builder__mobile-bar-thumb-center">' +
          '<div class="stack-builder__mobile-bar-thumb-inner" data-mobile-thumb-inner></div>' +
        '</div>';
      document.body.appendChild(thumb);
      return thumb;
    }

    // Keep all three "Add to cart" buttons — the original, the inline one,
    // and the mobile fixed bar's — in lockstep for label text and disabled
    // state, without repeating three assignments everywhere addToCart
    // touches them.
    // A second "Add to cart" sitting right under the running total, right
    // after the preview. On desktop the preview column is sticky, so this
    // rides along while the module grid scrolls by; on mobile the fixed
    // bottom bar backs it up once it scrolls out of view. This is also the
    // one and only "Add to cart" shown in compact mode, alongside the free
    // gift-wrap / gift-message options (kept here, rather than duplicated
    // per button, since every "Add to cart" trigger reads from this single
    // pair of checkboxes at click time regardless of which button fired it).
    function ensureInlineAddButton() {
      var existing = root.querySelector('[data-stack-add-inline]');
      if (existing) return existing.closest('.stack-builder__inline-cta');
      var totals = root.querySelector('.stack-builder__totals');
      var wrap = document.createElement('div');
      wrap.className = 'stack-builder__inline-cta';
      wrap.innerHTML =
        '<div class="stack-builder__gift-options" data-stack-gift-options>' +
          '<label class="stack-builder__gift-option">' +
            '<input type="checkbox" data-stack-gift-wrap>' +
            '<span>' + escapeHtml(labels.giftWrapLabel || 'Add free gift wrapping') + '</span>' +
          '</label>' +
          '<label class="stack-builder__gift-option">' +
            '<input type="checkbox" data-stack-gift-message-toggle>' +
            '<span>' + escapeHtml(labels.giftMessageLabel || 'Add a free personal message card') + '</span>' +
          '</label>' +
          '<textarea class="stack-builder__gift-message-input" data-stack-gift-message-input rows="2" placeholder="' + escapeAttr(labels.giftMessagePlaceholder || 'Write your message…') + '" hidden></textarea>' +
        '</div>' +
        '<button type="button" class="stack-builder__add-to-cart" data-stack-add-inline disabled>' +
          '<span data-stack-add-inline-label></span>' +
        '</button>';
      totals.parentNode.insertBefore(wrap, totals.nextSibling);
      return wrap;
    }

    function setAddLabel(text) {
      if (addToCartLabel) addToCartLabel.textContent = text;
      mobileAddLabel.textContent = text;
      inlineAddLabel.textContent = text;
    }

    function setAddDisabled(disabled) {
      if (addToCartBtn) addToCartBtn.disabled = disabled;
      mobileAddBtn.disabled = disabled;
      inlineAddBtn.disabled = disabled;
    }

    function render() {
      var top = stack.filter(function (s) { return s.role === 'top'; });
      var rest = stack.filter(function (s) { return s.role !== 'top'; });
      var ordered = top.concat(rest);

      preview.classList.toggle('has-stack', ordered.length > 0);

      if (ordered.length === 0) {
        preview.innerHTML = '';
        emptyState.hidden = false;
        preview.appendChild(emptyState);
      } else {
        emptyState.hidden = true;
        preview.innerHTML = '';
        ordered.forEach(function (s) {
          var layer = document.createElement('div');
          layer.className = 'stack-builder__preview-layer';

          var img = document.createElement('img');
          img.src = s.image;
          img.alt = s.moduleLabel + ' - ' + s.color;
          img.className = 'stack-builder__preview-layer-img';
          layer.appendChild(img);

          if (s.role !== 'top') {
            var grip = document.createElement('button');
            grip.type = 'button';
            grip.className = 'stack-builder__preview-layer-handle';
            grip.setAttribute('aria-label', labels.reorder || 'Drag to reorder');
            grip.innerHTML = '&#8942;&#8942;';
            layer.appendChild(grip);
            makeDraggable(grip, layer, s.key, preview);
          }

          preview.appendChild(layer);
        });
        stackLayers(preview).then(function () { updateMobileThumb(); });
      }

      preview.appendChild(downloadBtn);

      list.innerHTML = '';
      ordered.forEach(function (s) {
        var li = document.createElement('li');
        li.className = 'stack-builder__list-item';
        var handleMarkup = s.role !== 'top'
          ? '<button type="button" class="stack-builder__list-item-handle" aria-label="' + escapeAttr(labels.reorder || 'Drag to reorder') + '">&#8942;&#8942;</button>'
          : '<span class="stack-builder__list-item-handle stack-builder__list-item-handle--locked" aria-hidden="true"></span>';
        li.innerHTML = handleMarkup +
          '<span class="stack-builder__list-item-text">' + escapeHtml(s.moduleLabel) + ' — ' + escapeHtml(s.color) + '</span>' +
          '<span class="stack-builder__list-item-price">' + escapeHtml(s.priceFormatted) + '</span>' +
          '<button type="button" class="stack-builder__list-item-remove" aria-label="' + escapeAttr(labels.remove || 'Remove') + '">&times;</button>';
        li.querySelector('.stack-builder__list-item-remove').addEventListener('click', function () {
          removeStackKey(s.key);
        });
        if (s.role !== 'top') {
          makeDraggable(li.querySelector('.stack-builder__list-item-handle'), li, s.key, list);
        }
        list.appendChild(li);
      });

      var counts = {};
      stack.forEach(function (s) { counts[s.variantId] = (counts[s.variantId] || 0) + 1; });
      items.forEach(function (i) {
        var badge = i.el.querySelector('[data-item-badge]');
        var n = counts[i.variantId] || 0;
        if (n > 0) {
          badge.hidden = false;
          badge.textContent = n;
          i.el.classList.add('is-selected');
        } else {
          badge.hidden = true;
          i.el.classList.remove('is-selected');
        }
      });

      var total = stack.reduce(function (sum, s) { return sum + s.price; }, 0);
      countEl.textContent = stack.length + (stack.length === 1 ? ' module' : ' modules');
      priceEl.textContent = stack.length ? formatMoney(total) : '';
      mobileCountEl.textContent = countEl.textContent;
      mobilePriceEl.textContent = priceEl.textContent;
      mobileBar.classList.toggle('is-active', stack.length > 0);
      if (ordered.length === 0) updateMobileThumb();

      setAddDisabled(stack.length === 0);
      resetBtn.hidden = stack.length === 0;
      downloadBtn.hidden = stack.length === 0;
    }

    var CREATION_PREFIX = labels.creationPrefix || 'Creation';
    var creationNumber = 1;
    var creationsPanel = ensureCreationsPanel();

    function fetchCart() {
      return fetch((config.cartUrl || '/cart') + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); });
    }

    function parseCreationNumber(label) {
      var match = /(\d+)\s*$/.exec(String(label || ''));
      return match ? parseInt(match[1], 10) : null;
    }

    function ensureCreationsPanel() {
      var existing = root.querySelector('[data-stack-creations]');
      if (existing) return existing;
      var picker = root.querySelector('.stack-builder__picker') || errorEl.parentNode;
      var panel = document.createElement('div');
      panel.className = 'stack-builder__creations';
      panel.setAttribute('data-stack-creations', '');
      panel.hidden = true;
      panel.innerHTML = '<div class="stack-builder__creations-title">' +
        escapeHtml(labels.creationsTitle || 'Your creations in this cart') + '</div>' +
        '<ul class="stack-builder__creations-list" data-stack-creations-list></ul>' +
        '<a class="stack-builder__creations-view-cart" data-stack-creations-cart href="' + escapeAttr(config.cartUrl || '/cart') + '">' +
        escapeHtml(labels.viewCart || 'View cart') + '</a>';
      picker.insertBefore(panel, errorEl.nextSibling);
      return panel;
    }

    function refreshCreations() {
      fetchCart().then(function (cart) {
        var groups = {};
        var order = [];
        (cart.items || []).forEach(function (item) {
          var props = item.properties || {};
          var label = props['Creation'];
          if (!label) return;
          if (!groups[label]) {
            groups[label] = { label: label, build: props['Build'] || '', total: 0, count: 0 };
            order.push(label);
          }
          groups[label].total += item.line_price;
          groups[label].count += item.quantity;
        });

        var maxN = 0;
        order.forEach(function (label) {
          var n = parseCreationNumber(label);
          if (n && n > maxN) maxN = n;
        });
        creationNumber = maxN + 1;

        order.sort(function (a, b) {
          return (parseCreationNumber(a) || 0) - (parseCreationNumber(b) || 0);
        });

        var listEl = creationsPanel.querySelector('[data-stack-creations-list]');
        listEl.innerHTML = '';
        order.forEach(function (label) {
          var g = groups[label];
          var li = document.createElement('li');
          li.className = 'stack-builder__creations-item';
          li.innerHTML = '<span class="stack-builder__creations-item-label">' + escapeHtml(label) + '</span>' +
            '<span class="stack-builder__creations-item-recipe">' + escapeHtml(g.build) + '</span>' +
            '<span class="stack-builder__creations-item-price">' + escapeHtml(formatMoney(g.total)) + '</span>';
          listEl.appendChild(li);
        });
        creationsPanel.hidden = order.length === 0;
      }).catch(function () { /* non-critical, ignore */ });
    }

    refreshCreations();

    function addToCart() {
      if (stack.length === 0) return;
      errorEl.hidden = true;
      setAddDisabled(true);
      var originalLabel = addToCartLabel ? addToCartLabel.textContent : (labels.addToCart || '');
      setAddLabel(labels.adding || 'Adding…');

      var buildDescription = stack.map(function (s) { return s.moduleLabel + ' (' + s.color + ')'; }).join(' + ');
      var creationLabel = CREATION_PREFIX + ' ' + creationNumber;

      var grouped = {};
      stack.forEach(function (s) {
        grouped[s.variantId] = (grouped[s.variantId] || 0) + 1;
      });

      var giftProps = {};
      if (giftWrapCheckbox && giftWrapCheckbox.checked) {
        giftProps['Gift wrapping'] = 'Yes, please';
      }
      if (giftMessageToggle && giftMessageToggle.checked) {
        var giftMessageValue = giftMessageInput ? giftMessageInput.value.trim() : '';
        giftProps['Gift message'] = giftMessageValue || 'Yes, please (no message provided)';
      }

      var payload = {
        items: Object.keys(grouped).map(function (variantId) {
          var itemProperties = {
            'Creation': creationLabel,
            'Build': buildDescription
          };
          Object.keys(giftProps).forEach(function (key) {
            itemProperties[key] = giftProps[key];
          });
          return {
            id: variantId,
            quantity: grouped[variantId],
            properties: itemProperties
          };
        })
      };

      fetch(config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function () {
          setAddLabel(labels.added || 'Added to cart');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:update'));
          var drawer = document.querySelector('cart-drawer, cart-notification, #cart-drawer, #CartDrawer');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          }
          stack = [];
          if (giftWrapCheckbox) giftWrapCheckbox.checked = false;
          if (giftMessageToggle) giftMessageToggle.checked = false;
          if (giftMessageInput) {
            giftMessageInput.value = '';
            giftMessageInput.hidden = true;
          }
          render();
          refreshCreations();
          showToast('✓ ' + creationLabel + ' ' + (labels.addedSuffix || 'added to your cart'));
          setTimeout(function () { setAddLabel(originalLabel); }, 2500);
        })
        .catch(function (err) {
          errorEl.hidden = false;
          errorEl.textContent = (err && (err.description || err.message)) || labels.genericError || 'Something went wrong, please try again.';
          setAddLabel(originalLabel);
        })
        .finally(function () {
          setAddDisabled(stack.length === 0);
        });
    }

    items.forEach(function (i) {
      i.el.addEventListener('click', function () { addItem(i); });
    });

    tabs.forEach(function (tabBtn) {
      tabBtn.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        tabBtn.classList.add('is-active');
        tabBtn.setAttribute('aria-selected', 'true');
        currentTab = tabBtn.getAttribute('data-stack-tab');
        renderFilters();
      });
    });

    if (addToCartBtn) addToCartBtn.addEventListener('click', addToCart);
    mobileAddBtn.addEventListener('click', addToCart);
    inlineAddBtn.addEventListener('click', addToCart);
    resetBtn.addEventListener('click', resetStack);
    downloadBtn.addEventListener('click', downloadStackImage);
    if (compactCta) compactCta.addEventListener('click', saveHandoff);

    initThumbDrag();
    renderFilters();
    render();
    if (!isCompact) applyHandoffIfAny();
  }

  function init() {
    document.querySelectorAll('[data-stack-builder]').forEach(initStackBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector && e.target.querySelector('[data-stack-builder]');
    if (root) initStackBuilder(root);
  });
})();
